<template>
  <div class="min-h-screen flex items-center justify-center p-6 bg-zinc-950 relative overflow-hidden font-outfitSelection">
    <!-- Abstract Background Pattern -->
    <div class="absolute inset-0 opacity-20 pointer-events-none">
      <div class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500 rounded-full blur-[120px]"></div>
      <div class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-500 rounded-full blur-[120px]"></div>
    </div>

    <Card class="w-full max-w-[420px] shadow-2xl border-zinc-800 bg-zinc-900 text-white relative z-10 animate-in zoom-in-95 duration-500 overflow-hidden">
      <div class="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-emerald-500 to-indigo-500 animate-gradient-x"></div>
      
      <CardHeader class="space-y-6 pt-10 pb-8 transition-all duration-500">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center shadow-lg border border-zinc-700/50 group transition-all active:scale-95">
             <img :src="logo" alt="Finance Logo" class="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-500" />
          </div>
        </div>
        
        <div class="text-center space-y-2">
          <CardTitle class="text-3xl font-black tracking-tight uppercase">Codifyo <span class="text-indigo-400">Finance</span></CardTitle>
          <CardDescription class="text-zinc-500 font-bold italic tracking-wide" v-if="loginStep === 'credentials'">
            Portail de gestion patrimoniale
          </CardDescription>
          <CardDescription class="text-indigo-400 font-black uppercase tracking-widest text-[10px]" v-else-if="loginStep === '2fa'">
             Validation forte requise
          </CardDescription>
          <CardDescription class="text-emerald-400 font-black uppercase tracking-widest text-[10px]" v-else-if="loginStep === 'setup'">
             Configuration de sécurité
          </CardDescription>
          <CardDescription class="text-amber-400 font-black uppercase tracking-widest text-[10px]" v-else-if="loginStep === 'suggested'">
             Sécurité recommandée
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent class="pb-10">
        <!-- Step 1: Credentials -->
        <form v-if="loginStep === 'credentials'" @submit.prevent="handleLogin" class="space-y-5 animate-in slide-in-from-bottom-4 duration-500">
          <div class="space-y-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Identifiant Email</label>
            <div class="relative flex items-center">
              <Mail class="absolute left-3.5 w-4 h-4 text-zinc-600" />
              <input type="email" v-model="email" required placeholder="votre@email.com" 
                     class="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 pl-11 text-sm font-bold focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all placeholder:text-zinc-700" />
            </div>
          </div>
          
          <div class="space-y-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Mot de passe</label>
            <div class="relative flex items-center">
              <Lock class="absolute left-3.5 w-4 h-4 text-zinc-600" />
              <input type="password" v-model="password" required placeholder="••••••••••••" 
                     class="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 pl-11 text-sm font-bold focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all placeholder:text-zinc-700" />
            </div>
          </div>
          
          <div v-if="error" class="flex items-center gap-2 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold italic animate-shake">
            <AlertCircle class="w-4 h-4 shrink-0" /> {{ error }}
          </div>
          
          <Button type="submit" variant="finance" size="lg" shadow="xl" class="w-full h-12 text-sm font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-500" :disabled="loading">
            <RefreshCw v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
            Se Connecter <ChevronRight v-if="!loading" class="w-4 h-4 ml-2" />
          </Button>
        </form>

        <!-- Step 2: 2FA Verification -->
        <form v-else-if="loginStep === '2fa'" @submit.prevent="handleVerify2fa" class="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          <div class="p-4 bg-zinc-800/30 rounded-2xl border border-zinc-700 border-dashed text-center">
            <ShieldCheck class="w-8 h-8 text-indigo-400 mx-auto mb-3" />
            <p class="text-xs text-zinc-400 font-bold leading-relaxed px-4">Entrez le code à 6 chiffres de votre application de sécurité.</p>
          </div>
          
          <div class="space-y-2 px-6">
            <input type="text" v-model="otpCode" required maxlength="6" pattern="\d{6}" placeholder="000 000" 
                   class="w-full bg-zinc-800 text-center text-4xl font-black tracking-[0.4em] p-4 rounded-2xl border border-zinc-700 focus:ring-4 focus:ring-indigo-500/20 outline-none transition-all tabular-nums text-indigo-400 placeholder:text-zinc-800"
                   inputmode="numeric" autocomplete="one-time-code" />
          </div>
          
          <div v-if="error" class="flex items-center gap-2 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold text-center justify-center italic">
            <AlertCircle class="w-4 h-4 shrink-0" /> {{ error }}
          </div>
          
          <div class="space-y-3">
            <Button type="submit" variant="finance" size="lg" class="w-full h-12 text-sm font-black uppercase tracking-widest bg-indigo-600" :disabled="loading">
              <RefreshCw v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
              Vérifier l'Identité
            </Button>
            <Button type="button" variant="ghost" class="w-full text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white" @click="resetToLogin">
              <ArrowLeft class="w-3 h-3 mr-2" /> Retour à la connexion
            </Button>
          </div>
        </form>

        <!-- Step 3: 2FA Setup -->
        <div v-else-if="loginStep === 'setup'" class="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
          <div class="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
            <p class="text-[10px] text-emerald-400 font-black uppercase tracking-widest">Double Authentification Obligatoire</p>
          </div>
          
          <div class="flex justify-center p-3 bg-white rounded-2xl shadow-xl shadow-black/20">
             <qrcode-vue :value="qrCodeUri" :size="160" level="H" />
          </div>
          
          <div class="space-y-2 text-center px-4">
            <p class="text-[10px] text-zinc-500 font-bold uppercase leading-relaxed tracking-tight antialiased italic">
              1. Scannez avec Google Authenticator ou Authy<br>
              2. Saisissez le code de validation ci-dessous
            </p>
          </div>
          
          <form @submit.prevent="handleEnable2fa" class="space-y-4">
             <div class="px-8">
                 <input type="text" v-model="otpCode" required maxlength="6" placeholder="000000" 
                        class="w-full bg-zinc-800 text-center text-3xl font-black tracking-widest p-3 rounded-2xl border border-zinc-700 outline-none tabular-nums text-emerald-400" />
             </div>
             
             <div v-if="error" class="text-xs font-bold text-red-400 text-center p-2 bg-red-500/10 rounded-lg">
                {{ error }}
             </div>
             
             <div class="space-y-3 pt-2">
                <Button type="submit" variant="finance" size="lg" class="w-full h-12 text-xs font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 border-none" :disabled="loading">
                    {{ loading ? 'Activation...' : 'Finaliser la Sécurité' }}
                </Button>
                <Button type="button" variant="ghost" class="w-full text-[10px] font-bold uppercase text-zinc-500" @click="resetToLogin">Annuler</Button>
             </div>
          </form>
        </div>

        <!-- Step 4: 2FA Suggestion (Optional Setup) -->
        <div v-else-if="loginStep === 'suggested'" class="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
           <div class="p-4 bg-zinc-800/30 rounded-2xl border border-zinc-700 border-dashed text-center">
             <ShieldCheck class="w-8 h-8 text-amber-400 mx-auto mb-3 animate-pulse" />
             <p class="text-xs text-zinc-300 font-bold leading-relaxed px-4 mb-2">Protégez vos données et portefeuilles financiers.</p>
             <p class="text-[10px] text-zinc-500 leading-normal px-2">
               L'activation de la double authentification (2FA) ajoute une barrière de sécurité indispensable pour sécuriser vos informations de gestion de patrimoine.
             </p>
           </div>

           <div v-if="error" class="flex items-center gap-2 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-bold italic animate-shake">
             <AlertCircle class="w-4 h-4 shrink-0" /> {{ error }}
           </div>

           <div class="space-y-3 pt-2">
             <Button type="button" variant="finance" size="lg" class="w-full h-12 text-xs font-black uppercase tracking-widest bg-amber-600 hover:bg-amber-500 border-none flex items-center justify-center gap-2" @click="start2faSetupFromSuggestion" :disabled="loading">
               <Scan class="w-4 h-4" />
               {{ loading ? 'Chargement...' : 'Sécuriser mon compte' }}
             </Button>
             <Button type="button" variant="ghost" class="w-full h-10 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white" @click="skip2faSuggestion" :disabled="loading">
               Passer pour le moment
             </Button>
           </div>
         </div>
      </CardContent>
    </Card>

    <!-- UI Decor -->
    <div class="absolute bottom-8 right-8 flex items-center gap-4 text-zinc-800 pointer-events-none select-none">
       <div class="flex flex-col items-end">
         <span class="text-[10px] font-black uppercase tracking-[0.3em]">Module Financier</span>
         <span class="text-[8px] font-bold opacity-50 uppercase tracking-widest">v3.0.0-Zinc-Modern</span>
       </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import QrcodeVue from 'qrcode.vue'
import { 
  Lock, Mail, ChevronRight, 
  ShieldCheck, RefreshCw, AlertCircle, 
  ArrowLeft, CheckCircle2, Scan
} from 'lucide-vue-next'
import logo from '../../images/logo-finance.png'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card'

const email = ref('')
const password = ref('')
const otpCode = ref('')
const error = ref('')
const loading = ref(false)
const loginStep = ref('credentials') // 'credentials', '2fa', 'setup', 'suggested'
const suggestedToken = ref('')
const qrCodeUri = ref('')
const router = useRouter()

const handleLogin = async () => {
    error.value = ''
    loading.value = true
    
    try {
        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                email: email.value, 
                password: password.value,
                app: 'finance' // Mandatory 2FA triggered by this
            })
        })
        
        const data = await response.json()
        
        if (!response.ok) {
            error.value = data.error || 'Échec de la connexion'
            return
        }

        if (data.status === '2fa_required') {
            loginStep.value = '2fa'
            return
        }

        if (data.status === 'setup_2fa_required') {
            await fetchQrCode()
            loginStep.value = 'setup'
            return
        }

        if (data.status === 'setup_2fa_suggested') {
            suggestedToken.value = data.token
            loginStep.value = 'suggested'
            return
        }
        
        // Direct success (should not happen for Finance if logic is solid, but good fallback)
        completeLogin(data.token)
    } catch (err) {
        error.value = 'Une erreur réseau est survenue'
    } finally {
        loading.value = false
    }
}

const handleVerify2fa = async () => {
    error.value = ''
    loading.value = true
    try {
        const response = await fetch('/api/login/2fa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: email.value,
                password: password.value,
                code: otpCode.value
            })
        })
        const data = await response.json()
        if (!response.ok) {
            error.value = data.error || 'Code invalide'
            return
        }
        completeLogin(data.token)
    } catch (e) {
        error.value = 'Erreur lors de la vérification'
    } finally {
        loading.value = false
    }
}

const fetchQrCode = async () => {
    const response = await fetch('/api/2fa/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.value, password: password.value })
    })
    const data = await response.json()
    qrCodeUri.value = data.qrCodeUri
}

const handleEnable2fa = async () => {
    error.value = ''
    loading.value = true
    try {
        const response = await fetch('/api/2fa/enable', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: email.value,
                password: password.value,
                code: otpCode.value
            })
        })
        const data = await response.json()
        if (!response.ok) {
            error.value = data.error || 'Code invalide'
            return
        }
        completeLogin(data.token)
    } catch (e) {
        error.value = "Erreur lors de l'activation"
    } finally {
        loading.value = false
    }
}

const completeLogin = (token) => {
    localStorage.setItem('apiToken', token)
    router.push({ name: 'finance_home' })
}

const start2faSetupFromSuggestion = async () => {
    error.value = ''
    loading.value = true
    try {
        await fetchQrCode()
        loginStep.value = 'setup'
    } catch (err) {
        error.value = 'Erreur lors du chargement du QR Code'
    } finally {
        loading.value = false
    }
}

const skip2faSuggestion = () => {
    completeLogin(suggestedToken.value)
}

const resetToLogin = () => {
    loginStep.value = 'credentials'
    error.value = ''
    otpCode.value = ''
}
</script>

<style>
@keyframes gradient-x {
  0%, 100% { background-position: 0% 50%; opacity: 0.8; }
  50% { background-position: 100% 50%; opacity: 1; }
}
.animate-gradient-x {
  background-size: 200% 200%;
  animation: gradient-x 10s ease infinite;
}
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.2s cubic-bezier(.36,.07,.19,.97) both;
}
</style>

<style scoped>
/* No specific scoped styles needed anymore thanks to Tailwind */
</style>
